import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ClaireoaksLogo from '../ClaireoaksLogo';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Products', path: '/products' },
  { name: 'Contact', path: '/contact' },
];

const Navigation = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isHome = location.pathname === '/';
  const transparent = isHome && !isScrolled && !isMobileMenuOpen;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <nav
      className={`fixed inset-x-0 top-0 z-[100] transition-all duration-500 ${
        transparent
          ? 'bg-transparent'
          : 'border-b border-oak-900/5 bg-mist/95 shadow-soft backdrop-blur-md'
      }`}
    >
      <div className="mx-auto flex h-[4.5rem] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <ClaireoaksLogo light={transparent} />

        <div className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => {
            const active = location.pathname === link.path;
            return (
              <Link
                key={link.name}
                to={link.path}
                className={`relative px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${
                  transparent
                    ? active
                      ? 'text-white'
                      : 'text-white/75 hover:text-white'
                    : active
                      ? 'text-oak-900'
                      : 'text-oak-600 hover:text-oak-900'
                }`}
              >
                {link.name}
                {active ? (
                  <span
                    className={`absolute inset-x-3.5 -bottom-0.5 h-px ${
                      transparent ? 'bg-claire-300' : 'bg-claire-500'
                    }`}
                  />
                ) : null}
              </Link>
            );
          })}
          <div className="ml-5 flex items-center gap-2">
            <Link
              to="/login"
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                transparent
                  ? 'text-white hover:bg-white/10'
                  : 'text-oak-800 hover:bg-oak-100'
              }`}
            >
              Sign in
            </Link>
            <Link
              to="/signup"
              className={`rounded-lg px-4 py-2.5 text-sm font-semibold transition-all duration-300 ${
                transparent
                  ? 'bg-white text-oak-900 hover:bg-claire-50'
                  : 'bg-oak-900 text-white hover:bg-claire-700'
              }`}
            >
              Become a member
            </Link>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={`inline-flex rounded-lg p-2 md:hidden ${
            transparent ? 'text-white hover:bg-white/10' : 'text-oak-800 hover:bg-oak-100'
          }`}
          aria-expanded={isMobileMenuOpen}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      <div
        className={`absolute inset-x-0 top-[4.5rem] border-b border-oak-100 bg-mist/98 backdrop-blur-md transition-all duration-300 md:hidden ${
          isMobileMenuOpen
            ? 'pointer-events-auto translate-y-0 opacity-100'
            : 'pointer-events-none -translate-y-3 opacity-0'
        }`}
      >
        <div className="space-y-1 px-4 py-3">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`block rounded-lg px-4 py-3 text-base font-medium ${
                location.pathname === link.path
                  ? 'bg-oak-100 text-oak-900'
                  : 'text-oak-700 hover:bg-oak-50'
              }`}
            >
              {link.name}
            </Link>
          ))}
          <div className="grid gap-2 pt-3">
            <Link to="/login" className="rounded-lg px-4 py-3 text-center font-semibold text-oak-800 hover:bg-oak-50">
              Sign in
            </Link>
            <Link
              to="/signup"
              className="rounded-lg bg-oak-900 px-4 py-3 text-center font-semibold text-white hover:bg-claire-700"
            >
              Become a member
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
